import type {ExternalProvider, ExternalProviderName, ValueOf, ExternalProviderId} from '@myparcel-frontend/types';
import {useSdk} from '@myparcel-frontend/sdk';
import {toExternalProviderName} from '@myparcel-frontend/types';
import {dayjs} from '@myparcel-frontend/utils';
import {storeToRefs} from 'pinia';
import {useExternalProviderCredentialsStore} from '@/store/externalProviderCredentialsStore/externalProviderCredentialsStore';

export const useExternalCredentials = () => {
  const externalProviderStore = useExternalProviderCredentialsStore();
  const {externalProviderCredentials} = storeToRefs(externalProviderStore);

  const fetchCredentialsForShop = async (shopId: number, providerId: ValueOf<typeof ExternalProviderId>) => {
    const {sdk} = useSdk();

    const provider = toExternalProviderName(providerId);

    const map = {
      amazon: sdk.getExternalIntegrationsProviderCredentialsAmazon,
      bol_dot_com: sdk.getExternalIntegrationsProviderCredentialsBol,
      etsy: sdk.getExternalIntegrationsProviderCredentialsEtsy,
      exact_de: sdk.getExternalIntegrationsProviderCredentialsExactDe,
      exact: sdk.getExternalIntegrationsProviderCredentialsExact,
      wix: sdk.getExternalIntegrationsProviderCredentialsWix,
    };

    const credentials = await map[provider]({
      parameters: {shop_id: shopId},
    });

    credentials.forEach((credential) => {
      credential.shop_id = shopId;
    });

    return credentials;
  };

  const getCredentialsForShop = async (shopId: number, providerId: ValueOf<typeof ExternalProviderId>) => {
    const credentials = getProviderCredentials(shopId, providerId);

    if (credentials) {
      return credentials;
    }

    const fetchedCredentials = await fetchCredentialsForShop(shopId, providerId);

    if (!fetchedCredentials.length) {
      return;
    }

    externalProviderStore.addExternalProviderCredentials(fetchedCredentials);

    return getProviderCredentials(shopId, providerId);
  };

  const getShopCredentials = (shopId: number) => {
    return externalProviderCredentials.value.filter((externalProviderCredential) => {
      return externalProviderCredential.shop_id === shopId;
    });
  };

  const getProviderCredentials = (shopId: number, providerId: ValueOf<typeof ExternalProviderId>) => {
    const shopCredentials = getShopCredentials(shopId);
    return shopCredentials.find((credential) => {
      return credential.external_integration_provider_id === providerId;
    });
  };

  const updateCredentials = async (provider: ValueOf<typeof ExternalProviderName>, credentials: ExternalProvider) => {
    const {sdk} = useSdk();

    const map = {
      amazon: sdk.putExternalIntegrationsProviderCredentialsAmazon,
      bol_dot_com: sdk.putExternalIntegrationsProviderCredentialsBol,
      etsy: sdk.putExternalIntegrationsProviderCredentialsEtsy,
      exact_de: sdk.putExternalIntegrationsProviderCredentialsExactDe,
      exact: sdk.putExternalIntegrationsProviderCredentialsExact,
      wix: sdk.putExternalIntegrationsProviderCredentialsWix,
    };

    await map[provider]({
      body: [
        {
          auto_import: credentials.auto_import,
          id: credentials.id,
          shop_id: credentials.shop_id,
        },
      ],
    });
    externalProviderStore.updateExternalProviderCredentials(credentials);
  };

  const deleteCredentials = async (provider: ValueOf<typeof ExternalProviderName>, credentials: ExternalProvider) => {
    const {sdk} = useSdk();

    const map = {
      amazon: sdk.deleteExternalIntegrationsProviderCredentialsAmazon,
      bol_dot_com: sdk.deleteExternalIntegrationsProviderCredentialsBol,
      etsy: sdk.deleteExternalIntegrationsProviderCredentialsEtsy,
      exact_de: sdk.deleteExternalIntegrationsProviderCredentialsExactDe,
      exact: sdk.deleteExternalIntegrationsProviderCredentialsExact,
      wix: sdk.deleteExternalIntegrationsProviderCredentialsWix,
    };

    await map[provider]({
      path: {
        id: credentials.id,
      },
    });
    externalProviderStore.removeExternalProviderCredentials(credentials);
  };

  const createCredentials = async (
    shopId: number,
    provider: ValueOf<typeof ExternalProviderName>,
    returnUrl: string,
    credentials = {},
  ) => {
    const {sdk} = useSdk();

    const map = {
      amazon: sdk.postExternalIntegrationsProviderCredentialsAmazon,
      bol_dot_com: sdk.postExternalIntegrationsProviderCredentialsBol,
      etsy: sdk.postExternalIntegrationsProviderCredentialsEtsy,
      exact_de: sdk.postExternalIntegrationsProviderCredentialsExactDe,
      exact: sdk.postExternalIntegrationsProviderCredentialsExact,
      wix: sdk.postExternalIntegrationsProviderCredentialsWix,
    };

    const response = await map[provider]({
      body: [
        {
          shop_id: shopId,
          credentials,
        },
      ],
    });

    localStorage.setItem('returnUrl', returnUrl);
    window.location.assign(response);
  };

  const getCredentialsStatus = (credentials?: ExternalProvider) => {
    const isActive = Boolean(credentials?.active);

    const expirationDate = credentials?.expires ? dayjs(credentials?.expires) : null;
    const hasExpirationDate = expirationDate !== null && expirationDate.isValid();
    const isExpiringSoon =
      hasExpirationDate && dayjs().add(2, 'weeks').isAfter(expirationDate) && dayjs().isBefore(expirationDate);
    const isExpired = hasExpirationDate && dayjs().isAfter(expirationDate);

    return {
      isActive,
      isExpiringSoon,
      isExpired,
    };
  };

  return {
    getCredentialsForShop,
    getShopCredentials,
    getProviderCredentials,
    updateCredentials,
    deleteCredentials,
    createCredentials,
    getCredentialsStatus,
  };
};
